import { useEffect, useMemo, useState } from "react";
import type { Opportunity, OpportunityPricingSnapshot } from "@/types/opportunity";
import { OpportunityConfirmModal } from "@/components/opportunity/OpportunityConfirmModal";
import { useDualReadProductTypes } from "@/hooks/useDualReadProductTypes";
import { changeOpportunityProductType } from "@/lib/opportunity/opportunityClient";
import { resolveFormatCalcLines } from "@/lib/opportunity/formatCalcLineResolver";
import { resolveManagerPresetFormatKey } from "@/lib/opportunity/formatRegistry";
import {
  buildOpportunityProductTypeOptions,
  formatOpportunityProductTypeOptionLabel,
} from "@/lib/opportunity/productTypeLabel";
import { remapPricingCalcOverrides } from "@/lib/opportunity/remapPricingCalcOverrides";
import { listEffectiveServiceOfferings } from "@/lib/service/client";
import type { EffectiveServiceOffering } from "@/lib/service/types";

export function OpportunityProductTypeField(props: {
  opportunity: Opportunity;
  activeSnapshot: OpportunityPricingSnapshot | null;
  disabled?: boolean;
  onChanged: () => void;
  setMessage: (msg: { text: string; ok: boolean } | null) => void;
}) {
  const { opportunity, activeSnapshot, disabled, onChanged, setMessage } = props;
  const productTypes = useDualReadProductTypes();
  const [inheritedOfferings, setInheritedOfferings] = useState<EffectiveServiceOffering[]>([]);
  const [pendingType, setPendingType] = useState<string | null>(null);
  const [droppedLabels, setDroppedLabels] = useState<string[]>([]);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void listEffectiveServiceOfferings({ quotable_only: true }).then((res) => {
      if (!res.ok) return;
      setInheritedOfferings(res.data.offerings.filter((o) => o.is_inherited));
    });
  }, [productTypes]);

  const productTypeOptions = useMemo(() => {
    const opts = buildOpportunityProductTypeOptions(productTypes, inheritedOfferings);
    if (!opts.some((o) => o.value === opportunity.product_type)) {
      return [{ value: opportunity.product_type, label: opportunity.product_type }, ...opts];
    }
    return opts;
  }, [productTypes, inheritedOfferings, opportunity.product_type]);

  async function beginChange(next: string) {
    if (next === opportunity.product_type) return;
    setError(null);
    setPendingType(next);
    setDroppedLabels([]);
    if (!activeSnapshot) return;
    setPreviewBusy(true);
    const previousFormat = resolveManagerPresetFormatKey(opportunity.product_type);
    const nextFormat = resolveManagerPresetFormatKey(next);
    const [previousLines, nextLines] = await Promise.all([
      resolveFormatCalcLines({ formatKey: previousFormat, productType: opportunity.product_type }),
      resolveFormatCalcLines({ formatKey: nextFormat, productType: next }),
    ]);
    const remapped = remapPricingCalcOverrides({
      previousLines,
      nextLines,
      values: activeSnapshot.format_calc_values ?? {},
      modes: activeSnapshot.format_calc_modes ?? {},
      notes: activeSnapshot.format_calc_deviation_notes ?? {},
    });
    setDroppedLabels(remapped.droppedLabels);
    setPreviewBusy(false);
  }

  async function confirmChange() {
    if (!pendingType) return;
    setBusy(true);
    setError(null);
    const r = await changeOpportunityProductType({
      opportunity_id: opportunity.id,
      product_type: pendingType,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    const lost = r.data.dropped_labels;
    setPendingType(null);
    setMessage({
      text: lost.length
        ? `Product type updated. Dropped incompatible lines: ${lost.join(", ")}.`
        : r.data.reran_pricing
          ? "Product type updated. Compatible pricing was carried forward."
          : "Product type updated.",
      ok: true,
    });
    onChanged();
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 py-1 min-w-0">
        <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 w-24 shrink-0">
          Product
        </span>
        <select
          value={pendingType ?? opportunity.product_type}
          onChange={(e) => void beginChange(e.target.value)}
          disabled={disabled || busy}
          className="min-w-0 flex-1 max-w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
        >
          {productTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {formatOpportunityProductTypeOptionLabel(opt)}
            </option>
          ))}
        </select>
      </div>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {error}
        </div>
      ) : null}
      <OpportunityConfirmModal
        open={pendingType != null}
        title="Change product type?"
        confirmLabel={previewBusy ? "Checking compatibility…" : "Yes, change type"}
        busy={busy || previewBusy}
        onConfirm={() => void confirmChange()}
        onCancel={() => {
          setPendingType(null);
          setDroppedLabels([]);
        }}
      >
        <p>Are you sure you want to do that?</p>
        <p>
          Pricing will be rerun for the new product type. Compatible fields (subtotal, tax,
          deposit, extra items, and matching calculation lines) are kept.
        </p>
        <p>
          You might lose information that is not compatible with the new product type
          {droppedLabels.length > 0
            ? `: ${droppedLabels.join(", ")}`
            : " (product-specific calculation lines, document-slot requirements, and todos)"}.
        </p>
      </OpportunityConfirmModal>
    </div>
  );
}
